import { slugify } from "./string";
import type { EventItem, SessionType } from "@/data/core";
import { formatLocation } from "@/util/formatters";
import { getUtcMidnightTimestamp } from "./date";

export function sessionSlug(title: string) {
  return slugify(title);
}

/**
 * The distinct /session/[slug] routes for a session list. Shared by
 * generateStaticParams, the sitemaps, and sitemap.md so every published
 * session page is discoverable from one definition.
 */
export function uniqueSessionSlugs(sessions: { title: string }[]): string[] {
  return [...new Set(sessions.map((session) => sessionSlug(session.title)))];
}

export function occurrenceId(
  eventName: string,
  title: string,
  occurrenceIndex?: number,
) {
  const baseId = `${slugify(eventName)}__${sessionSlug(title)}`;
  return occurrenceIndex === undefined
    ? baseId
    : `${baseId}__${occurrenceIndex}`;
}

export type SessionRow = {
  id: string;
  date: string;
  startDateTimestamp: number;
  endDate: string;
  endDateTimestamp: number;
  event: string;
  eventWebsite: string | null;
  location: string | null;
  title: string;
  type: SessionType;
  languageCode: "en" | "de" | null;
  slotLabel: string | null;
  durationMinutes: number | [number, number] | null;
  slidesUrl: string | null;
  recordingUrl: string | null;
  codeUrl: string | null;
};

export function buildSessions(events: EventItem[]): SessionRow[] {
  return events
    .flatMap((event) => {
      const startDateTimestamp = getUtcMidnightTimestamp(event.startDate);

      const endDate = event.endDate ?? event.startDate;
      const endDateTimestamp = getUtcMidnightTimestamp(endDate);
      const titleCounts = new Map<string, number>();
      const titleSeen = new Map<string, number>();

      for (const session of event.talks) {
        titleCounts.set(
          session.title,
          (titleCounts.get(session.title) ?? 0) + 1,
        );
      }

      return event.talks.map((sess) => {
        const duplicateCount = titleCounts.get(sess.title) ?? 0;
        const currentIndex = titleSeen.get(sess.title) ?? 0;
        titleSeen.set(sess.title, currentIndex + 1);

        return {
          id: occurrenceId(
            event.name,
            sess.title,
            duplicateCount > 1 ? currentIndex + 1 : undefined,
          ),
          date: event.startDate,
          startDateTimestamp,
          endDate,
          endDateTimestamp,
          event: event.name,
          eventWebsite: event.website || null,
          location: formatLocation(event.location),
          title: sess.title,
          type: sess.type,
          languageCode: sess.languageCode ?? null,
          slotLabel: sess.slotLabel ?? null,
          durationMinutes: sess.durationMinutes ?? null,
          slidesUrl: sess.slidesUrl ?? null,
          recordingUrl: sess.recordingUrl ?? null,
          codeUrl: sess.codeUrl ?? null,
        };
      });
    })
    .sort((a, b) => b.startDateTimestamp - a.startDateTimestamp);
}
